import PropTypes from "prop-types";
import React, { Fragment } from "react";
import styled from "styled-components";
import { RemoveCircle } from "@mui/icons-material";

import { Tr, Td } from "commons";
import { convertToMoney } from "commons/src/Money";
import { isPrintMode } from "commons/src/QueryParameter";
import { InfoPopover, InfoDetail, useInfoPopover } from "commons/src/InfoPopover";

import { FolioTable } from "../FolioDetails/FolioTable";
import { HeadedCard } from "../../components/HeadedCard";
import { getRemainingAllowance } from "../../helpers/MoneyHelpers";

const PENSION_DISCLAIMER =
    "The annual allowance above relates to your standard overall allowance across all of your pension accounts with Nucleus and other providers. The allowance used and allowance remaining values above relate to your Nucleus pension account(s) only. If you have other pension accounts with other providers, your adviser will be able to guide you on what further contributions you can make.";
const ONE_HUNDRED = 100;
const CONTRIBUTIONS_BAR_EVEN_RADIUS_WIDTH = 100;

export function WrapperContributions({ wrapperDetails, isPension, hasMultiple }) {
    const {
        airdAnnualLimit,
        totalSubscriptionCTY,
        replaceableWithdrawals,
        returnedReplaceableWithdrawals
    } = wrapperDetails;
    const replaceableWithdrawalAllowance = replaceableWithdrawals - returnedReplaceableWithdrawals;

    const totalAllowance = airdAnnualLimit + (replaceableWithdrawalAllowance || 0);
    const totalSubscriptionDataSeries =
        (ONE_HUNDRED * Math.min(totalAllowance, totalSubscriptionCTY)) / totalAllowance;
    const [anchorElement, popoverContent, openInfoPopover, closeInfoPopover] = useInfoPopover();

    return (
        <Fragment>
            <BarContainer>
                <Bar data-testid="allowance-bar">
                    <TotalContributionsBar
                        data-testid="contributions-bar"
                        width={totalSubscriptionDataSeries}
                    />
                    <BarBackground />
                </Bar>
            </BarContainer>
            <InfoPopover
                handleClose={closeInfoPopover}
                anchorEl={anchorElement}
                content={popoverContent}
                horizontalPosition="left"
            />
            <FolioTable dense={isPrintMode()}>
                <tbody>
                    <Tr>
                        <Td align="left">Annual allowance</Td>
                        <Td align="right">{convertToMoney(airdAnnualLimit)}</Td>
                    </Tr>
                    {replaceableWithdrawalAllowance > 0 && [
                        <Tr key="replaceable-withdrawal-allowance">
                            <FlexRow align="left">
                                Replaceable withdrawal allowance
                                <InfoDetail
                                    data-testid="replacement-info-button"
                                    onClick={e => openInfoPopover(e, replacementWithdrawalInfo)}
                                />
                            </FlexRow>
                            <Td align="right">{convertToMoney(replaceableWithdrawalAllowance)}</Td>
                        </Tr>,
                        <Tr key="total-allowance">
                            <Td align="left">Total allowance</Td>
                            <Td align="right">{convertToMoney(totalAllowance)}</Td>
                        </Tr>
                    ]}
                    <Tr>
                        <Td align="left">
                            <ColourFilledSquare opacity="1" />
                            Allowance used at Nucleus
                        </Td>
                        <Td align="right">{convertToMoney(totalSubscriptionCTY)}</Td>
                    </Tr>
                    <Tr>
                        <Td align="left">
                            <ColourFilledSquare opacity="0.15" />
                            Allowance remaining
                        </Td>
                        <Td align="right">
                            {getRemainingAllowance(totalAllowance, totalSubscriptionCTY, isPension)}
                        </Td>
                    </Tr>
                    {hasMultiple && !isPension && (
                        <tr>
                            <td>
                                <InfoHeading data-testid="info-heading">
                                    The allowances above take into account all of your Nucleus Isa
                                    accounts. Withdrawals from and contributions to your other Isa
                                    accounts are included in these calculations
                                </InfoHeading>
                            </td>
                        </tr>
                    )}
                </tbody>
            </FolioTable>
            {isPension && <DisclaimerContainer>{PENSION_DISCLAIMER}</DisclaimerContainer>}
        </Fragment>
    );
}

const replacementWithdrawalInfo = () => ({
    header: <Heading>Replaceable withdrawal allowance</Heading>,
    body: (
        <>
            <Paragraph data-testid="info-popover-paragraph-1">
                Nucleus Isa accounts are flexible. This means that any withdrawals made from a
                Nucleus Isa can be replaced back into the account without affecting your annual
                allowance, as long as they’re paid back into the Isa in the same tax year they were
                withdrawn. These withdrawals are known as ‘replaceable withdrawals’.
            </Paragraph>
            <Paragraph data-testid="info-popover-paragraph-2">
                Your replaceable withdrawal allowance is the amount of money that has been withdrawn
                within the current tax year that has not been returned to your Nucleus Isa. This
                replaceable withdrawal allowance is added to your normal annual allowance to show
                your total allowance for the current tax year.
            </Paragraph>
            <HeadedCard title="How it's calculated">
                <Formula>
                    <FormulaParagraph>
                        Total replaceable withdrawals in the current tax year
                    </FormulaParagraph>
                    <FormulaIcon>
                        <RemoveCircle fontSize="small" aria-label="minus" />
                    </FormulaIcon>
                    <FormulaParagraph>
                        replaceable withdrawals that have been repaid in the current tax year.
                    </FormulaParagraph>
                </Formula>
            </HeadedCard>
        </>
    )
});

const Heading = styled.h1`
    font-size: 1.25rem;
`;

const Paragraph = styled.p`
    font-size: 1rem;
    text-align: justify;
`;

const BarContainer = styled.div`
    margin-top: 14px;
`;

export const DisclaimerContainer = styled.div`
    font-size: 0.8em;
    padding: 0 22px 10px 22px;
`;

const InfoHeading = styled.span`
    font-size: 0.8em;
    color: ${props => props.theme.secondary};
`;

const FlexRow = styled(Td)`
    display: flex;
    align-items: center;
`;

const Formula = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;

    @media (max-width: 620px) {
        flex-direction: column;
    }
`;

const FormulaIcon = styled.div`
    display: flex;
    margin: 1rem;

    @media (max-width: 620px) {
        margin: 0.5rem;
    }
`;

const FormulaParagraph = styled.p`
    font-size: 0.875rem;
    margin: 0;
`;

export const Bar = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
`;

export const TotalContributionsBar = styled.div`
    height: 32px;
    width: ${props => props.width}%;
    background-color: ${props => props.theme.primary};
    border-radius: ${props =>
        props.width === CONTRIBUTIONS_BAR_EVEN_RADIUS_WIDTH ? "5px" : "5px 0px 0px 5px"};
    position: absolute;
    top: 0;
    z-index: 1;
`;

const BarBackground = styled.div`
    height: 32px;
    width: 100%;
    background-color: ${props => props.theme.primary};
    opacity: 0.15;
    border-radius: 5px;
`;

export const ColourFilledSquare = styled.div`
    background-color: ${props => props.theme.primary};
    opacity: ${props => props.opacity};
    display: inline-block;
    border-radius: 3px;
    position: relative;
    margin: 0 8px -2px 0;
    height: 14px;
    width: 14px;
`;

WrapperContributions.propTypes = {
    wrapperDetails: PropTypes.object,
    hasMultiple: PropTypes.bool,
    isPension: PropTypes.bool
};
